import { useEffect } from "react";
import LandingNav from "@/components/landing/LandingNav";
import HeroSection from "@/components/landing/HeroSection";
import WhyHeirWiseSection from "@/components/landing/WhyHeirWiseSection";
import HowItWorksSection from "@/components/landing/HowItWorksSection";
import ProductPreviewsSection from "@/components/landing/ProductPreviewsSection";
import ParentValueSection from "@/components/landing/ParentValueSection";
import PracticalSkillsSection from "@/components/landing/PracticalSkillsSection";
import FAQSection from "@/components/landing/FAQSection";
import CTASection from "@/components/landing/CTASection";
import LandingFooter from "@/components/landing/LandingFooter";
import SEO from "@/components/SEO";

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "What is HeirWise?", acceptedAnswer: { "@type": "Answer", text: "HeirWise is an Economic & Enterprise Education platform for teens and families. Teens practice practical decisions across money, economics, business, and entrepreneurship through guided lessons, assessments, and simulations, while parents get progress insight." } },
    { "@type": "Question", name: "What's included in a HeirWise plan?", acceptedAnswer: { "@type": "Answer", text: "Every plan includes adaptive lessons, decision practice, independent assessments, simulations, and parent progress reporting." } },
    { "@type": "Question", name: "How is HeirWise different from a traditional class?", acceptedAnswer: { "@type": "Answer", text: "Teens practice decisions and apply concepts instead of only memorizing definitions. Lessons combine explanation, decision practice, assessment, feedback, and parent-visible progress." } },
    { "@type": "Question", name: "Can parents see what their teen is learning?", acceptedAnswer: { "@type": "Answer", text: "Yes. Parents get a private insight view that shows lesson progress, confidence gaps, and conversation prompts — without watching every click." } },
    { "@type": "Question", name: "What age is HeirWise for?", acceptedAnswer: { "@type": "Answer", text: "HeirWise accounts are designed for users ages 13 and older, with parent-managed access." } },
    { "@type": "Question", name: "How much does HeirWise cost?", acceptedAnswer: { "@type": "Answer", text: "Individual is $24.99/month or $249/year for one teen. Family is $39.99/month or $399/year for up to four teens. There is no free trial." } },
  ],
};

const Landing = () => {
  useEffect(() => {
    document.body.classList.add("landing-dark");
    return () => document.body.classList.remove("landing-dark");
  }, []);

  return (
    <div className="relative min-h-screen w-full bg-[#0c0a1f] text-zinc-100 overflow-x-hidden">
      {/* Global ambient canvas */}
      <div aria-hidden className="pointer-events-none fixed inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(70,55,140,0.55),transparent_60%),radial-gradient(ellipse_at_bottom,rgba(30,40,90,0.55),transparent_55%)]" />
        <div className="absolute inset-0 opacity-[0.05] bg-[linear-gradient(rgba(255,255,255,0.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.6)_1px,transparent_1px)] bg-[size:80px_80px]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_60%,rgba(0,0,0,0.45)_100%)]" />
      </div>

      {/* Section-transition glow layer (scrolls with content for ambient depth) */}
      <div aria-hidden className="pointer-events-none absolute inset-0 z-0">
        <div className="absolute top-[40vh] -left-32 h-[520px] w-[520px] rounded-full bg-[radial-gradient(circle,rgba(34,211,238,0.14),transparent_60%)] blur-3xl" />
        <div className="absolute top-[110vh] -right-32 h-[560px] w-[560px] rounded-full bg-[radial-gradient(circle,rgba(167,139,250,0.16),transparent_60%)] blur-3xl" />
        <div className="absolute top-[200vh] left-1/2 -translate-x-1/2 h-[680px] w-[680px] rounded-full bg-[radial-gradient(circle,rgba(99,102,241,0.16),transparent_60%)] blur-3xl" />
        <div className="absolute top-[310vh] -left-20 h-[520px] w-[520px] rounded-full bg-[radial-gradient(circle,rgba(251,191,36,0.10),transparent_60%)] blur-3xl" />
      </div>

      <SEO
        title="HeirWise | Economic & Enterprise Education for Teens"
        description="HeirWise is Economic & Enterprise Education for teens: an adaptive learning system where teens practice real-world money, business, and decision-making skills while parents get insight — without micromanaging."
        path="/"
        jsonLd={faqJsonLd}
      />

      <div className="relative z-10 [&_section]:scroll-mt-24">
        <LandingNav />
        <HeroSection />
        <WhyHeirWiseSection />
        <HowItWorksSection />
        <div id="learning-experience"><ProductPreviewsSection /></div>
        <ParentValueSection />
        <PracticalSkillsSection />
        <FAQSection />
        <CTASection />
        <LandingFooter />
      </div>
    </div>
  );
};

export default Landing;
